import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Download, Upload, MessageCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { api, errMsg } from "../lib/api";

const saveCsv = (rows, name) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const blob = new Blob([rows.map((r) => r.map(esc).join(",")).join("\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = name; a.click();
};

const waLink = (r) => `https://wa.me/${r.phone}?text=${encodeURIComponent(`Assalam-o-Alaikum ${r.name}, your CommissionWorld Sales Executive account is ready. Sales ID: ${r.sales_id}. Set your password here (valid 3 days): ${window.location.origin}${r.setup_path}`)}`;

export const ImportExecutivesDialog = ({ onDone }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/executives/import", fd);
      setRes(data); onDone();
      toast.success(`${data.created} executive(s) added`);
    } catch (e2) { toast.error(errMsg(e2)); }
    setBusy(false); e.target.value = "";
  };
  const created = res?.results.filter((r) => r.status === "created") || [];
  const exportLinks = () => saveCsv([["name", "email", "sales_id", "setup_link"], ...created.map((r) => [r.name, r.email, r.sales_id, window.location.origin + r.setup_path])], "executive_setup_links.csv");
  return (
    <Dialog onOpenChange={(o) => !o && setRes(null)}>
      <DialogTrigger asChild><Button variant="outline" data-testid="import-exec-button"><FileSpreadsheet className="mr-2 h-4 w-4" />Import spreadsheet</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle>Import Sales Executives</DialogTitle></DialogHeader>
        {!res ? (
          <div className="space-y-4 text-sm">
            <p className="text-slate-600">Upload a <b>.csv</b> or <b>.xlsx</b> file. The first row must have the headers <span className="font-mono">name, email, phone</span> and optionally <span className="font-mono">city</span>. Up to 500 rows.</p>
            <div className="rounded-lg border bg-slate-50 p-3 font-mono text-xs">name,email,phone,city<br />Ayesha Malik,ayesha@gmail.com,0300-1234567,Lahore</div>
            <p className="text-slate-600">Each person gets a Sales ID and a private set-password link (valid 3 days). Rows with an invalid or already-registered email are skipped.</p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => saveCsv([["name", "email", "phone", "city"], ["Ayesha Malik", "ayesha@example.com", "0300-1234567", "Lahore"]], "executives_template.csv")} data-testid="import-template-download"><Download className="mr-2 h-4 w-4" />Download template</Button>
              <input ref={ref} type="file" accept=".csv,.xlsx" className="hidden" onChange={upload} data-testid="import-file-input" />
              <Button disabled={busy} onClick={() => ref.current.click()} data-testid="import-upload-button"><Upload className="mr-2 h-4 w-4" />{busy ? "Importing…" : "Choose file and import"}</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 text-sm" data-testid="import-results">
            <div className="flex flex-wrap items-center gap-4">
              <span className="font-semibold text-emerald-700" data-testid="import-created-count">{res.created} added</span>
              <span className="font-semibold text-amber-700" data-testid="import-skipped-count">{res.skipped} skipped</span>
              {created.length > 0 && <Button size="sm" variant="outline" onClick={exportLinks} data-testid="import-export-links"><Download className="mr-2 h-4 w-4" />Download setup links (CSV)</Button>}
            </div>
            <p className="text-xs text-slate-500">Send each person their own link (WhatsApp button below). You can also email a link later from Manage → "Generate &amp; email password reset link".</p>
            <div className="divide-y rounded-lg border">
              {res.results.map((r) => (
                <div key={r.row} className="flex items-center justify-between gap-3 p-3" data-testid={`import-row-${r.row}`}>
                  <div className="min-w-0"><p className="truncate font-medium">{r.name || "—"} <span className="font-normal text-slate-500">{r.email}</span></p>
                    <p className="text-xs">{r.status === "created" ? <span className="font-mono text-emerald-700">{r.sales_id}</span> : <span className="text-amber-700">Row {r.row}: {r.reason}</span>}</p></div>
                  {r.status === "created" && <Button asChild size="sm" variant="outline"><a href={waLink(r)} target="_blank" rel="noreferrer" data-testid={`import-wa-${r.row}`}><MessageCircle className="mr-1 h-4 w-4" />WhatsApp</a></Button>}
                </div>
              ))}
            </div>
            <Button variant="ghost" onClick={() => setRes(null)}>Import another file</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
